/**
 * Когда предлагать включить уведомления — чистое правило, без хранилища и
 * системных запросов.
 *
 * Живёт в `shared/`, а не у каждого клиента, потому что читателей двое — веб и
 * натив, — и правило у них обязано быть одно. Две копии такой бухгалтерии
 * разъедутся при первой правке, а заметить это можно будет только по жалобам
 * живых людей: «просит каждый раз» или, наоборот, «я так и не включил».
 *
 * ## Почему не «один раз» и не «каждый раз»
 *
 * Один раз — это то, как было сделано сначала, и это слишком робко: человек,
 * которому в тот момент было не до того, теряется навсегда. Замерено на
 * боевом 2026-08-26: уведомления включены у ОДНОГО человека из 21.
 *
 * Каждый раз — навязчиво, и хуже того: полоса перестаёт читаться, её начинают
 * закрывать не глядя, а вместе с ней закроют и всё остальное, что мы туда
 * когда-нибудь поместим.
 *
 * Поэтому середина: три попытки за всё время, разнесённые и по числу
 * раскладов, и по календарю. Оба условия обязаны выполниться разом — человек,
 * сделавший двадцать раскладов за вечер, не должен получить все три попытки
 * подряд.
 *
 * ## Что считается ответом
 *
 * Только нажатие «Включить». После него мы показываем системный запрос, а он
 * бывает один раз за установку: что бы человек в нём ни ответил, спрашивать
 * больше нечем и незачем. «Позже» ответом не считается — это и есть перенос.
 */

/** Сколько раскладов до первой попытки. Один: раньше напоминать не о чем. */
export const FIRST_AFTER = 1
/** Сколько раскладов должно набежать между попытками. */
export const AGAIN_AFTER = 5
/** И сколько дней — ближе этого не просим ни при каких счётчиках. */
export const AGAIN_AFTER_DAYS = 5
/** Больше этого числа попыток не делаем никогда. */
export const MAX_ASKS = 3

export interface NotifyState {
  /** Сколько раз показывали полосу. */
  asks: number
  /** Когда показывали в прошлый раз, unix мс. 0 — не показывали. */
  askedAt: number
  /** Сколько было раскладов в момент прошлого показа. */
  readingsAtAsk: number
  /** Человек нажал «Включить» — дальше решает система, а не мы. */
  done: boolean
}

export const EMPTY_NOTIFY: NotifyState = { asks: 0, askedAt: 0, readingsAtAsk: 0, done: false }

/** Показывать ли полосу прямо сейчас. */
export function shouldAskNotify(s: NotifyState, readings: number, now: number): boolean {
  if (s.done || s.asks >= MAX_ASKS) return false
  if (readings < FIRST_AFTER) return false
  if (s.askedAt === 0) return true

  const enoughReadings = readings >= s.readingsAtAsk + AGAIN_AFTER
  const enoughDays = now - s.askedAt >= AGAIN_AFTER_DAYS * 24 * 3600 * 1000
  return enoughReadings && enoughDays
}

/** Полоса показана и закрыта переносом («Позже» или уход с экрана). */
export function noteAsked(s: NotifyState, readings: number, now: number): NotifyState {
  return { ...s, asks: s.asks + 1, askedAt: now, readingsAtAsk: readings }
}

/** Человек нажал «Включить» — больше не спрашиваем никогда. */
export function noteDone(s: NotifyState): NotifyState {
  return { ...s, done: true }
}
